import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";

const Grievance = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex-grow bg-white py-8 md:py-12">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl font-bold mb-6 text-center text-[#2D0052]">
              Grievance Redressal & Data-Protection Contact
            </h1>
            <p className="text-sm text-gray-500 text-center mb-8">
              Last updated: Jun 02, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#D9008D]">
                1. Introduction & Scope
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                This section explains how TTL Media Pvt. Ltd. ("Oye Creators,"
                "we," "us") handles any questions, concerns or complaints
                ("grievances") about the collection, use, disclosure or
                retention of personal data. It applies to all three user
                groups on our marketplace platform:
              </p>
              <ul className="list-disc ml-6 space-y-2 mt-4">
                <li className="text-base leading-relaxed text-gray-800">
                  Brands & Businesses offering "gigs" to creators
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Creators who execute gigs
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Oye Creators employees & admins
                </li>
              </ul>
              <p className="text-base leading-relaxed text-gray-800 mt-4">
                All data-protection activities comply with:
              </p>
              <ul className="list-disc ml-6 space-y-2 mt-4">
                <li className="text-base leading-relaxed text-gray-800">
                  India's Information Technology Act, 2000 & SPDI Rules, 2011
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  India's Digital Personal Data Protection Act, 2023 ("DPDP
                  Act")
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  EU General Data Protection Regulation (GDPR), where relevant
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Any other applicable national or international privacy law.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                2. Definitions
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  Personal Data: Any information relating to an identified or
                  identifiable person (e.g., name, email, social-media
                  handles, IP address).
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Processing: Any operation performed on Personal Data
                  (collection, storage, retrieval, disclosure, erasure).
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Data Subject: The individual whose personal data is
                  processed.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Data Protection Officer (DPO): The person responsible for
                  overseeing our compliance and serving as your primary
                  contact.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                3. Your Rights & Legal Basis
              </h2>
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full border-collapse border border-solid border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Right
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Description
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Legal Reference
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2 font-medium">
                        Access & Portability
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Request a copy of the Personal Data we hold about you,
                        in a commonly used format you can transfer elsewhere.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        GDPR Art. 15–20; DPDP §13, §14
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2 font-medium">
                        Rectification (Correction)
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Request updates to inaccurate or incomplete data.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        GDPR Art. 16; DPDP §13(2)
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2 font-medium">
                        Erasure (Right to be Forgotten)
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Request deletion of your data when it's no longer
                        needed, or you withdraw consent (with exceptions for
                        legal compliance).
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        GDPR Art. 17; DPDP §15
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2 font-medium">
                        Restriction of Processing
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Ask us to pause any processing while we verify a
                        dispute over accuracy or lawfulness.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        GDPR Art. 18; DPDP §16
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2 font-medium">
                        Objection & Consent Withdrawal
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Object to processing based on legitimate interest or
                        withdraw consent to any processing you previously
                        agreed to.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        GDPR Art. 21; DPDP §5(3), §10
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2 font-medium">
                        Complaint to Supervisory Authority
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Lodge a complaint with the Indian Data Protection
                        Authority (IDPA) or your EU supervisory authority if
                        unsatisfied with our response.
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        GDPR Art. 77; DPDP §23
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                4. Data Protection Officer & Contact Details
              </h2>
              <p className="mb-2">
                <strong>Name:</strong> Ms. Anjali Mehta
              </p>
              <p className="mb-2">
                <strong>Title:</strong> Chief Compliance & Privacy Officer
                (Data Protection Officer)
              </p>
              <p className="mb-2">
                <strong>Email:</strong> dpo@oyecreators.com
              </p>
              <p className="mb-4">
                <strong>Phone:</strong> +91 20 1234 5678
              </p>

              <p className="mb-2">
                <strong>Postal Address:</strong>
              </p>
              <p className="mb-1">TTL Media Pvt. Ltd.</p>
              <p className="mb-1">"Oye Creators" Office, 4th Floor,</p>
              <p className="mb-4">
                TechPark Towers, Pune, Maharashtra 411045, India
              </p>

              <p className="mb-2">
                <strong>Alternate Contact (if DPO is unavailable):</strong>
              </p>
              <p className="mb-2">
                <strong>Name:</strong> Mr. Rajesh Kulkarni, Legal Head
              </p>
              <p className="mb-2">
                <strong>Email:</strong> legal@oyecreators.com
              </p>
              <p className="mb-2">
                <strong>Phone:</strong> +91 20 8765 4321
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                5. Filing a Grievance: Step-by-Step Flow
              </h2>
              <ul className="list-disc ml-6 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Prepare Your Request:</strong> Identify yourself:
                  full name, registered email, user type
                  (Brand/Creator/Employee). Clearly state the nature of your
                  request. Provide relevant IDs and attach supporting
                  documents.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Submit to the DPO:</strong> Email your request to
                  dpo@oyecreators.com with subject line beginning "Privacy
                  Grievance:" or "Data-Protection Request:". Postal mail
                  address as above.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Acknowledgment (Day 0–3):</strong> Within 3 business
                  days, you'll receive confirmation of receipt, a unique
                  ticket number, and an estimated timeline.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Investigation & Resolution (Day 3–30):</strong> Our
                  team conducts a thorough review and aims to provide a full
                  written response within 30 calendar days.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Extension (if necessary):</strong> For complex
                  requests, we may extend by an additional 30 days, per GDPR
                  Art. 12(3) and DPDP §18(4).
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  <strong>Closure & Record-Keeping:</strong> We'll send a
                  final report and retain all records for five years as
                  required by DPDP §22 and IT SPDI Rule 8.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                6. Escalation Matrix
              </h2>
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full border-collapse border border-solid border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Stage
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Contact
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Timeline
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Outcome
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Level 1 (Initial)
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        DPO, Ms. Mehta
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Ack within 3 business days; resolution in 30 days
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2"></td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Level 2 (Review)
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Legal Head, Mr. Kulkarni
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Within 7 days of your escalation
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2"></td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Level 3 (External)
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Supervisory Authority (IDPA / EU DPA)
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Per that authority's SLA
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                7. Grounds for Refusal & Exceptions
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                We may refuse or partially refuse a request if compliance
                would infringe on another person's rights, data is required
                for legal claims, or if exempt under applicable law. Any
                refusal will be communicated in writing, citing the specific
                legal basis.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                8. Limitation of Liability & Indemnification
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                By using Oye Creators, you agree to indemnify and hold
                harmless Oye Creators, TTL Media Pvt. Ltd., and our officers,
                directors, employees, and agents against any claim, loss or
                damage arising from misrepresentation, fraudulent
                documentation, or any data you provide that infringes
                third-party rights.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#D9008D]">
                9. Continuous Improvement & Review
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                This grievance-redressal process is reviewed at least once per
                year or sooner if legal standards change. Updates will be
                published here, and the "Last updated" date will appear at the
                top.
              </p>
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Grievance
